// src/App.tsx - Clean version without client registration
import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Button } from "@mantine/core";
import LandingPage from "./components/landing/LandingPage";
import AdminPortal from "./components/admin/AdminPortal";
import AdminLogin from "./components/admin/AdminLogin";
import TrainerPortal from "./components/trainer/TrainerPortal";
import TrainerSelection from "./components/trainer/TrainerSelection";
import LoginPage from "./components/auth/LoginPage";
import ResetPassword from "./components/auth/ResetPassword";
import ClientPortal from "./components/client/ClientPortal";
import TrainerSetup from "./components/trainer/TrainerSetup";
import PaymentSuccess from "./components/payment/PaymentSuccess";
import TrainerPaymentSuccess from "./components/payment/TrainerPaymentSuccess";
import VideoCallPage from "./components/session/VideoCallPage";
import PostSessionForm from "./components/session/PostSessionForm";
import apiService from "./services/api";
import type { Trainer, SessionOptions, VerificationData, TrainerLoginData } from "./types";

function App(): React.ReactElement {
  const navigate = useNavigate();
  const _location = useLocation();
  const [currentPage, setCurrentPage] = useState<string>("landing");
  const [isTrainerLoggedIn, setIsTrainerLoggedIn] = useState<boolean>(false);
  const [isClientLoggedIn, setIsClientLoggedIn] = useState<boolean>(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState<boolean>(false);
  const [isTrainerSetup, setIsTrainerSetup] = useState<boolean>(false);
  const [isDevelopmentMode] = useState<boolean>(true);
  const [selectedTrainer, setSelectedTrainer] = useState<Trainer | null>(null);
  const [sessionOptions, setSessionOptions] = useState<SessionOptions | null>(null);
  const [trainerLoginData, setTrainerLoginData] = useState<TrainerLoginData>({
    email: "",
    password: "",
  });
  const [verificationData, setVerificationData] = useState<VerificationData>({
    email: "",
    captchaVerified: false,
    agreeToTerms: false,
  });

  // Session persistence - check localStorage on component mount
  useEffect(() => {
    console.log("=== APP COMPONENT MOUNTED - CHECKING SESSION PERSISTENCE ===");
    console.log("localStorage contents:", localStorage);

    // Check admin login status separately (only affects /admin routes)
    if (localStorage.getItem("adminLoggedIn") === "true") {
      setIsAdminLoggedIn(true);
    }

    const token = localStorage.getItem("token");
    const username = localStorage.getItem("username");
    const trainerId = localStorage.getItem("trainerId");
    const clientId = localStorage.getItem("clientId");

    console.log("Session data found:", { token, username, trainerId, clientId });

    // If we have a token and user data, restore the session (only for client/trainer, not admin)
    if (token && username) {
      console.log("Valid session found, restoring user state...");

      if (trainerId && !clientId) {
        // User is a trainer
        console.log("Restoring trainer session");
        setIsTrainerLoggedIn(true);
        setCurrentPage("trainer");
      } else if (clientId && !trainerId) {
        // User is a client
        console.log("Restoring client session");
        setIsClientLoggedIn(true);
        setCurrentPage("client-portal");
      } else if (trainerId && clientId) {
        // This shouldn't happen, but if it does, prioritize trainer
        console.log("Both trainer and client IDs found, prioritizing trainer");
        setIsTrainerLoggedIn(true);
        setCurrentPage("trainer");
      } else {
        // User is logged in but no specific portal ID - check if they need setup
        console.log("User logged in but no portal ID found, checking profile completion...");
        // Check if user needs to complete trainer setup
        checkUserSetupStatus(username, token);
      }
    } else {
      console.log("No valid session found, staying on landing page");
    }
  }, []); // Empty dependency array means this runs once on mount

  // Check if user needs to complete setup (for trainers)
  const checkUserSetupStatus = async (username: string, _token: string): Promise<void> => {
    try {
      console.log("Checking setup status for user:", username);

      // Try to get trainer ID to see if profile is completed
      const response = await apiService.getTrainerID(username);
      console.log("Trainer ID check response:", response);

      if (response.Code === 200 && response.id) {
        // Trainer profile exists, store ID and go to trainer portal
        localStorage.setItem("trainerId", response.id as string);
        setIsTrainerLoggedIn(true);
        setCurrentPage("trainer");
        console.log("Trainer profile found, redirecting to trainer portal");
      } else {
        // No trainer profile found, might need setup
        console.log("No trainer profile found, checking if user needs setup");
        setCurrentPage("landing");
      }
    } catch (error) {
      console.error("Error checking setup status:", error);
      setCurrentPage("landing");
    }
  };

  const handleCaptchaVerification = (): void => {
    const randomMath = Math.floor(Math.random() * 10) + 1;
    const randomMath2 = Math.floor(Math.random() * 10) + 1;
    const answer = prompt(`Human Verification: What is ${randomMath} + ${randomMath2}?`);

    if (parseInt(answer || "0") === randomMath + randomMath2) {
      setVerificationData({ ...verificationData, captchaVerified: true });
      alert("Human verification complete!");
    } else {
      alert("Incorrect answer. Please try again.");
    }
  };

  const _handleTrainerLogin = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    console.log("Trainer login attempt:", trainerLoginData);

    if (trainerLoginData.email && trainerLoginData.password) {
      setIsTrainerLoggedIn(true);
      setCurrentPage("trainer");
      alert("Login successful! Welcome back to your trainer portal.");
    } else {
      alert("Please enter both email and password.");
    }
  };

  const _handleTrainerPortalClick = (): void => {
    if (isDevelopmentMode) {
      setIsTrainerLoggedIn(true);
      setCurrentPage("trainer");
      console.log("Development mode: Skipping login, going directly to trainer portal");
    } else if (isTrainerLoggedIn) {
      setCurrentPage("trainer");
    } else {
      setCurrentPage("login");
    }
  };

  const handleLogout = (): void => {
    // Clear all state
    setIsTrainerLoggedIn(false);
    setIsClientLoggedIn(false);
    setIsTrainerSetup(false);
    setTrainerLoginData({ email: "", password: "" });
    setVerificationData({ email: "", captchaVerified: false, agreeToTerms: false });
    setCurrentPage("landing");

    // Clear any stored authentication data
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("username");
    localStorage.removeItem("trainerId");
    localStorage.removeItem("clientId");

    console.log("=== LOGOUT COMPLETE ===");

    // Navigate to login page
    navigate("/login");
  };

  const handleTrainerVerification = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (!verificationData.email.trim()) {
      alert("Please enter your email address.");
      return;
    }

    if (!verificationData.captchaVerified) {
      alert("Please complete the human verification.");
      return;
    }

    if (!verificationData.agreeToTerms) {
      alert("Please agree to the terms and conditions.");
      return;
    }

    alert(
      "Verification request submitted! We'll review your credentials and get back to you within 24 hours.",
    );
    setCurrentPage("landing");
  };

  const handleNavigateToClient = (): void => {
    navigate("/login");
  };

  const handleNavigateToTrainer = (): void => {
    navigate("/login");
  };

  const handleNavigateToAdmin = (): void => {
    navigate("/admin-login");
  };

  const handleSelectTrainer = (trainer: Trainer, options: SessionOptions): void => {
    setSelectedTrainer(trainer);
    setSessionOptions(options);
    navigate("/session");
  };

  const handleBackToClientPortal = (): void => {
    navigate("/client-portal");
  };

  const handleLoginSuccess = (userType: string): void => {
    console.log("=== HANDLE LOGIN SUCCESS CALLED ===");
    console.log("User type:", userType);

    if (userType === "trainer") {
      console.log("Redirecting to trainer portal...");
      setIsTrainerLoggedIn(true);
      navigate("/trainer");
    } else if (userType === "client") {
      console.log("Redirecting to client portal...");
      setIsClientLoggedIn(true);
      navigate("/client-portal");
    } else if (userType === "trainer-setup") {
      console.log("Redirecting to trainer setup...");
      setIsTrainerLoggedIn(true);
      navigate("/trainer-setup");
    } else {
      console.log("Redirecting to selection...");
      navigate("/selection");
    }
  };

  const handleBackToHome = (): void => {
    console.log("=== HANDLING BACK TO HOME ===");
    navigate("/");
    setIsTrainerLoggedIn(false);
    setIsClientLoggedIn(false);
    setIsAdminLoggedIn(false);
    setIsTrainerSetup(false);
    setSelectedTrainer(null);
    setSessionOptions(null);
    setTrainerLoginData({ email: "", password: "" });
    setVerificationData({ email: "", captchaVerified: false, agreeToTerms: false });
    localStorage.clear();
    console.log("Cleared all state and localStorage, returning to landing page");
  };

  // Trainer Verification Page
  if (currentPage === "trainer-verification") {
    return (
      <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
        <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border max-w-[500px] w-full mx-4 sm:mx-auto">
          <button
            onClick={() => setCurrentPage("landing")}
            className="bg-transparent border-none text-muted-foreground text-base cursor-pointer mb-5 flex items-center gap-2 hover:text-foreground transition-colors"
          >
            &larr; Back to Home
          </button>

          <div className="text-center mb-8">
            <div className="w-[60px] h-[60px] bg-gradient-to-r from-primary to-primary-hover rounded-xl flex items-center justify-center mx-auto mb-4">
              <img
                src="/images/logo.png"
                alt="Pheefee Logo"
                className="w-[60px] h-[60px] object-contain"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Trainer Verification
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed">
              Submit your credentials for verification
            </p>
          </div>

          <form onSubmit={handleTrainerVerification}>
            <div className="mb-6">
              <label className="block font-semibold text-foreground mb-2">
                Professional Email *
              </label>
              <input
                type="email"
                value={verificationData.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setVerificationData({ ...verificationData, email: e.target.value })
                }
                placeholder="your.email@professional.com"
                className="w-full py-3 px-4 bg-input border-2 border-border rounded-lg text-base box-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition outline-none"
                required
              />
            </div>

            <div className="mb-6">
              <button
                type="button"
                onClick={handleCaptchaVerification}
                className={`w-full py-3 px-4 border-2 rounded-lg text-base cursor-pointer transition ${
                  verificationData.captchaVerified
                    ? "bg-green-100 border-green-200 text-green-800"
                    : "bg-muted border-border text-muted-foreground hover:border-primary/40"
                }`}
              >
                {verificationData.captchaVerified
                  ? "Human Verified"
                  : "Complete Human Verification"}
              </button>
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={verificationData.agreeToTerms}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVerificationData({ ...verificationData, agreeToTerms: e.target.checked })
                  }
                  className="scale-125 accent-primary"
                />
                <span className="text-foreground text-sm">
                  I agree to the Terms of Service and Privacy Policy
                </span>
              </label>
            </div>

            <Button type="submit" fullWidth radius="md" size="lg">
              Submit for Verification
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            <LandingPage
              onNavigateToClient={handleNavigateToClient}
              onNavigateToTrainer={handleNavigateToTrainer}
              onNavigateToAdmin={handleNavigateToAdmin}
            />
          }
        />

        <Route
          path="/admin-login"
          element={
            <AdminLogin
              onLoginSuccess={() => {
                setIsAdminLoggedIn(true);
                navigate("/admin");
              }}
              onBackToHome={handleBackToHome}
            />
          }
        />

        <Route
          path="/admin"
          element={
            isAdminLoggedIn || localStorage.getItem("adminLoggedIn") === "true" ? (
              <AdminPortal
                onBackToHome={handleBackToHome}
                onLogout={() => {
                  localStorage.removeItem("adminLoggedIn");
                  setIsAdminLoggedIn(false);
                  handleLogout();
                  navigate("/admin-login");
                }}
              />
            ) : (
              <Navigate to="/admin-login" replace />
            )
          }
        />

        <Route
          path="/trainer-selection"
          element={
            <TrainerSelection
              onBackToClientPortal={handleBackToClientPortal}
              onSelectTrainer={handleSelectTrainer}
            />
          }
        />

        <Route
          path="/session"
          element={
            <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5 text-foreground">
              <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 text-center border border-border shadow-lg max-w-[600px] w-full mx-4 sm:mx-auto">
                <h1 className="text-2xl sm:text-3xl mb-5 text-primary font-bold">
                  Session Starting Soon
                </h1>
                <p className="text-lg sm:text-xl mb-8 text-muted-foreground">
                  Your session with {selectedTrainer?.name} is being prepared.
                </p>
                <div className="bg-accent-subtle border border-accent/25 rounded-xl p-5 mb-8 text-left">
                  <h3 className="text-accent font-semibold mb-2">Session Details</h3>
                  <p className="text-foreground">
                    Duration: {sessionOptions?.sessionDuration} minutes
                  </p>
                  <p className="text-foreground">
                    Cost: $
                    {selectedTrainer
                      ? (
                          (selectedTrainer.first10MinRate || 0) +
                          ((sessionOptions?.sessionDuration || 0) - 10) *
                            (selectedTrainer.perMinuteRate || 0)
                        ).toFixed(2)
                      : "0.00"}
                  </p>
                  {sessionOptions?.reviewPreviousSessions && (
                    <p className="text-primary">Previous sessions will be reviewed</p>
                  )}
                </div>
                <Button onClick={handleBackToClientPortal} radius="md" size="lg">
                  Back to Client Portal
                </Button>
              </div>
            </div>
          }
        />

        <Route
          path="/login"
          element={
            <LoginPage onBackToHome={handleBackToHome} onLoginSuccess={handleLoginSuccess} />
          }
        />

        <Route path="/reset-password" element={<ResetPassword onBackToHome={handleBackToHome} />} />

        <Route
          path="/client-portal"
          element={
            <ClientPortal
              onBackToHome={() => navigate("/")}
              onLogout={handleLogout}
              isLoggedIn={isClientLoggedIn}
              onFindTrainer={() => navigate("/trainer-selection")}
            />
          }
        />

        <Route
          path="/trainer"
          element={(() => {
            console.log("=== TRAINER PORTAL ACCESS ===");
            console.log("isClientLoggedIn:", isClientLoggedIn);
            console.log("isTrainerLoggedIn:", isTrainerLoggedIn);
            console.log("isTrainerSetup:", isTrainerSetup);
            console.log("localStorage trainerId:", localStorage.getItem("trainerId"));

            return (
              <TrainerPortal
                onBackToHome={handleBackToHome}
                onLogout={handleLogout}
                isLoggedIn={isTrainerLoggedIn}
              />
            );
          })()}
        />

        <Route
          path="/trainer-setup"
          element={
            <TrainerSetup
              onBackToHome={handleBackToHome}
              onSetupComplete={() => {
                setIsTrainerLoggedIn(true);
                navigate("/trainer");
              }}
              userData={null}
            />
          }
        />

        <Route path="/video-call" element={<VideoCallPage />} />
        <Route path="/post-session" element={<PostSessionForm />} />

        <Route
          path="/trainer/connect/complete"
          element={
            <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
              <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 max-w-[500px] w-full text-center shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border mx-4 sm:mx-auto">
                <div className="text-4xl sm:text-6xl mb-5">&#x2705;</div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                  Setup Complete
                </h1>
                <p className="text-lg mb-8 text-muted-foreground">
                  Your bank account has been connected. You can now receive session earnings.
                </p>
                <Button onClick={() => navigate("/trainer")} radius="md">
                  Back to Trainer Portal
                </Button>
              </div>
            </div>
          }
        />
        <Route
          path="/trainer/connect/refresh"
          element={
            <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
              <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 max-w-[500px] w-full text-center shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border mx-4 sm:mx-auto">
                <div className="text-4xl sm:text-6xl mb-5">&#x1F504;</div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                  Session Expired
                </h1>
                <p className="text-lg mb-8 text-muted-foreground">
                  Your onboarding session has expired. Please try again from your trainer portal.
                </p>
                <Button onClick={() => navigate("/trainer")} radius="md">
                  Back to Trainer Portal
                </Button>
              </div>
            </div>
          }
        />

        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/trainer-setup/payment-success" element={<TrainerPaymentSuccess />} />
        <Route
          path="/payment/cancel"
          element={
            <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
              <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 max-w-[500px] w-full text-center shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground mx-4 sm:mx-auto">
                <div className="text-4xl sm:text-6xl mb-5">&#x26A0;&#xFE0F;</div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-destructive">
                  Payment Cancelled
                </h1>
                <p className="text-lg mb-8 text-muted-foreground">
                  Your payment was cancelled. No charges were made.
                </p>
                <Button onClick={() => navigate("/client-portal")} radius="md">
                  Return to Portal
                </Button>
              </div>
            </div>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
